import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useDispatch } from 'react-redux';
import { addSermon, updateSermon, deleteSermon } from '../store/sermonSlice';
import { navigationBooks, krvBooks } from './constants/navigationBooks';

// Custom Page Break blot so editors can insert explicit page splits
const BlockEmbed = Quill.import('blots/block/embed');
class PageBreakBlot extends BlockEmbed {
  static blotName = 'page-break';
  static tagName = 'hr';
  static className = 'quill-page-break';
}
Quill.register(PageBreakBlot);

const getVerseNumber = (verseId) => {
  const numericVerseId = Number(verseId);
  if (!numericVerseId) return '';
  return numericVerseId % 1000;
};

const getBookPrefix = (verseId) => {
  const numericVerseId = Number(verseId);
  if (!numericVerseId) return 0;
  return Math.floor(numericVerseId / 1000000);
};

const buildVerseId = (bookPrefix, chapter, verse) => {
  const safeChapter = Number.parseInt(chapter, 10);
  const safeVerse = Number.parseInt(verse, 10);
  if (!bookPrefix || !safeChapter || !safeVerse) return null;
  return bookPrefix * 1000000 + safeChapter * 1000 + safeVerse;
};

const stripHtml = (value = '') => value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
const isKoreanVersion = (version) => ['KRV', 'SAE'].includes(version);

const getRangeRank = (chapter, verse) => {
  const safeChapter = Number.parseInt(chapter, 10);
  const safeVerse = Number.parseInt(verse, 10);
  if (!safeChapter || !safeVerse) return null;
  return safeChapter * 1000 + safeVerse;
};

const FieldError = ({ message }) => {
  if (!message) return null;
  return <p className="mt-1 text-sm text-red-600">{message}</p>;
};

const ConfirmationDialog = ({ children, confirmLabel, onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
    <div className="bg-white rounded-lg shadow-lg p-6 w-11/12 max-w-sm">
      <p className="text-lg font-semibold text-gray-800">{children}</p>
      <div className="flex justify-end space-x-4 mt-6">
        <button
          onClick={onConfirm}
          className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600"
        >
          {confirmLabel}
        </button>
        <button
          onClick={onCancel}
          className="bg-gray-300 text-black px-4 py-2 rounded-md hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
);

const StatusBanner = ({ tone, message }) => {
  if (!message) return null;

  const toneClassName =
    tone === 'error'
      ? 'bg-red-50 text-red-700 border border-red-200'
      : tone === 'info'
        ? 'bg-blue-50 text-blue-700 border border-blue-200'
        : 'bg-green-50 text-green-700 border border-green-200';

  return <div className={`mb-4 rounded-md px-4 py-3 text-sm ${toneClassName}`}>{message}</div>;
};

const EditorActions = ({
  hasSermon,
  isDeleting,
  isSaving,
  onClose,
  onDelete,
  onSave,
}) => (
  <div className="flex justify-end space-x-2">
    <button
      onClick={onSave}
      disabled={isSaving}
      className="px-4 py-2 bg-[#04657a] text-white rounded-md hover:bg-[#034857] disabled:cursor-not-allowed disabled:opacity-60"
    >
      {isSaving ? 'Saving...' : 'Save'}
    </button>
    <button
      onClick={onClose}
      className="px-4 py-2 bg-[#b87a25] text-white rounded-md hover:bg-[#915d13]"
    >
      Close
    </button>
    {hasSermon && (
      <button
        onClick={onDelete}
        disabled={isDeleting}
        className="px-4 py-2 bg-[#a83820] text-white rounded-md hover:bg-[#732311] disabled:cursor-not-allowed disabled:opacity-60"
      >
        Delete
      </button>
    )}
  </div>
);

const SermonEditor = ({
  columns,
  sermon,
  selectedVersion,
  selectedBook,
  selectedChapter,
  selectedVerseId,
  setIsSermonEditorOpen,
  setColumns,
  setSelectedTab,
}) => {
  const [sermonTitle, setSermonTitle] = useState('');
  const [sermonSpeaker, setSermonSpeaker] = useState('');
  const [startChapter, setStartChapter] = useState(selectedChapter || '');
  const [startVerseNumber, setStartVerseNumber] = useState(getVerseNumber(selectedVerseId));
  const [endChapter, setEndChapter] = useState('');
  const [endVerseNumber, setEndVerseNumber] = useState('');
  const [sermonText, setSermonText] = useState('');
  const [paginationType, setPaginationType] = useState('word'); // 'word' or 'line'
  const [isDirty, setIsDirty] = useState(false); // Track unsaved changes
  const [showConfirmClose, setShowConfirmClose] = useState(false); // Track close confirmation dialog
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusTone, setStatusTone] = useState('success');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [endChapterVerseOptions, setEndChapterVerseOptions] = useState([]);
  const dispatch = useDispatch();
  const quillRef = useRef(null);
  const hasRestoredDraftRef = useRef(false);
  const autosaveTimeoutRef = useRef(null);

  const wordsPerPage = 400; // Number of words per page
  const linesPerPage = 20; // Number of lines per page
  const selectedBookPrefix = getBookPrefix(selectedVerseId);
  const bookCatalog = useMemo(
    () => (isKoreanVersion(selectedVersion) ? krvBooks : navigationBooks),
    [selectedVersion]
  );
  const selectedBookMeta = useMemo(
    () => bookCatalog.find((book) => book.name === selectedBook) || null,
    [bookCatalog, selectedBook]
  );
  const availableEndChapters = useMemo(() => {
    const minimumChapter = Number.parseInt(startChapter || selectedChapter, 10) || 1;
    const maximumChapter = selectedBookMeta?.chapters || minimumChapter;
    const chapterCount = Math.max(maximumChapter - minimumChapter + 1, 1);

    return Array.from({ length: chapterCount }, (_, index) => String(minimumChapter + index));
  }, [selectedBookMeta, selectedChapter, startChapter]);
  const filteredEndVerseOptions = useMemo(() => {
    const normalizedStartChapter = Number.parseInt(startChapter, 10);
    const normalizedEndChapter = Number.parseInt(endChapter, 10);
    const normalizedStartVerse = Number.parseInt(startVerseNumber, 10);

    return endChapterVerseOptions.filter((verseOption) => {
      if (normalizedStartChapter !== normalizedEndChapter) return true;
      return Number(verseOption.verse) >= normalizedStartVerse;
    });
  }, [endChapterVerseOptions, endChapter, startChapter, startVerseNumber]);
  const draftStorageKey = useMemo(() => {
    const draftIdentity = sermon?.id
      ? `existing-${sermon.id}`
      : `new-${selectedVersion}-${selectedBook}-${selectedVerseId}`;

    return `sermon-editor-draft:${draftIdentity}`;
  }, [sermon?.id, selectedBook, selectedVerseId, selectedVersion]);

  // Memoize Quill modules so ReactQuill doesn't recreate the editor every render,
  // which can trigger react-quill's regeneration path and the "delta undefined" error.
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: '1' }, { header: '2' }, { font: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        ['link', 'image', 'video'],
        [{ pageBreak: 'insert' }],
        ['clean'],
      ],
      handlers: {
        pageBreak() {
          const editor = this.quill;
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, 'page-break', true, 'user');
          editor.setSelection(range.index + 1, 0);
        },
      },
    },
  }), []);

  // Initialize the form with sermon data
  useEffect(() => {
    if (sermon) {
      setSermonTitle(sermon.title || '');
      setSermonSpeaker(sermon.speaker || '');
      setSermonText(sermon.content || '');
      setStartChapter(sermon.start_chapter || selectedChapter || '');
      setStartVerseNumber(getVerseNumber(sermon.start_verse_id) || getVerseNumber(selectedVerseId));
      setEndChapter(sermon.end_chapter || '');
      setEndVerseNumber(getVerseNumber(sermon.end_verse_id));
    } else {
      setSermonTitle('');
      setSermonSpeaker('');
      setSermonText('');
      setStartChapter(selectedChapter || '');
      setStartVerseNumber(getVerseNumber(selectedVerseId));
      setEndChapter(selectedChapter || '');
      setEndVerseNumber(getVerseNumber(selectedVerseId));
    }
    setIsDirty(false); // Reset dirty state when opening
    setFormErrors({});
    setStatusMessage(null);
    setStatusTone('success');
    hasRestoredDraftRef.current = false;
  }, [sermon, selectedChapter, selectedVerseId]);

  useEffect(() => {
    if (!availableEndChapters.includes(String(endChapter))) {
      setEndChapter(availableEndChapters[0] || '');
    }
  }, [availableEndChapters, endChapter]);

  useEffect(() => {
    let isActive = true;

    const loadVerseOptions = async () => {
      const normalizedEndChapter = Number.parseInt(endChapter, 10);

      if (!selectedBook || !selectedVersion || !normalizedEndChapter) {
        if (isActive) setEndChapterVerseOptions([]);
        return;
      }

      const matchingColumn = Array.isArray(columns)
        ? columns.find(
            (column) =>
              column.book === selectedBook &&
              String(column.chapter) === String(normalizedEndChapter) &&
              column.version === selectedVersion &&
              Array.isArray(column.verses) &&
              column.verses.length > 0
          )
        : null;

      if (matchingColumn) {
        if (isActive) {
          setEndChapterVerseOptions(
            matchingColumn.verses.map((verse) => ({
              verse: String(verse.verse),
              verseId: verse.verse_id,
            }))
          );
        }
        return;
      }

      try {
        const response = await fetch(
          `/api/search?book=${encodeURIComponent(selectedBook)}&chapter=${normalizedEndChapter}&version=${encodeURIComponent(selectedVersion)}`
        );
        const results = await response.json();

        if (!response.ok) {
          throw new Error(results?.error || 'Failed to load verse options.');
        }

        if (isActive) {
          setEndChapterVerseOptions(
            Array.isArray(results)
              ? results.map((verse) => ({
                  verse: String(verse.verse),
                  verseId: verse.verse_id,
                }))
              : []
          );
        }
      } catch (error) {
        console.error('Error loading sermon verse options:', error);
        if (isActive) {
          setEndChapterVerseOptions([]);
        }
      }
    };

    loadVerseOptions();

    return () => {
      isActive = false;
    };
  }, [columns, endChapter, selectedBook, selectedVersion]);

  useEffect(() => {
    if (filteredEndVerseOptions.length === 0) return;

    const hasCurrentValue = filteredEndVerseOptions.some(
      (verseOption) => String(verseOption.verse) === String(endVerseNumber)
    );

    if (!hasCurrentValue) {
      setEndVerseNumber(String(filteredEndVerseOptions[0].verse));
    }
  }, [endVerseNumber, filteredEndVerseOptions]);

  useEffect(() => {
    if (typeof window === 'undefined' || hasRestoredDraftRef.current) return;

    const savedDraft = window.localStorage.getItem(draftStorageKey);
    if (!savedDraft) return;

    try {
      const parsedDraft = JSON.parse(savedDraft);
      const baseUpdatedAt = parsedDraft?.baseUpdatedAt;
      if (
        sermon?.updated_at &&
        baseUpdatedAt &&
        new Date(baseUpdatedAt).getTime() <= new Date(sermon.updated_at).getTime()
      ) {
        window.localStorage.removeItem(draftStorageKey);
        hasRestoredDraftRef.current = true;
        return;
      }

      setSermonTitle(parsedDraft?.title || '');
      setSermonSpeaker(parsedDraft?.speaker || '');
      setStartChapter(parsedDraft?.startChapter || selectedChapter || '');
      setStartVerseNumber(parsedDraft?.startVerseNumber || getVerseNumber(selectedVerseId));
      setEndChapter(parsedDraft?.endChapter || selectedChapter || '');
      setEndVerseNumber(parsedDraft?.endVerseNumber || getVerseNumber(selectedVerseId));
      setSermonText(parsedDraft?.content || '');
      setPaginationType(parsedDraft?.paginationType || 'word');
      setIsDirty(true);
      setStatusMessage('Restored your unsaved sermon draft.');
      setStatusTone('info');
    } catch (error) {
      console.error('Error restoring sermon draft:', error);
      window.localStorage.removeItem(draftStorageKey);
    } finally {
      hasRestoredDraftRef.current = true;
    }
  }, [draftStorageKey, selectedChapter, selectedVerseId, sermon?.updated_at]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    if (autosaveTimeoutRef.current) {
      window.clearTimeout(autosaveTimeoutRef.current);
    }

    if (!isDirty) {
      return undefined;
    }

    autosaveTimeoutRef.current = window.setTimeout(() => {
      window.localStorage.setItem(
        draftStorageKey,
        JSON.stringify({
          title: sermonTitle,
          speaker: sermonSpeaker,
          startChapter,
          startVerseNumber,
          endChapter,
          endVerseNumber,
          content: sermonText,
          paginationType,
          baseUpdatedAt: sermon?.updated_at || null,
          savedAt: new Date().toISOString(),
        })
      );
    }, 500);

    return () => {
      if (autosaveTimeoutRef.current) {
        window.clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [
    draftStorageKey,
    endChapter,
    endVerseNumber,
    isDirty,
    paginationType,
    sermon?.updated_at,
    sermonSpeaker,
    sermonText,
    sermonTitle,
    startChapter,
    startVerseNumber,
  ]);

  const estimatedPageCount = useMemo(() => {
    const plainText = stripHtml(sermonText);
    if (!plainText) return 1;

    if (paginationType === 'line') {
      const lines = plainText.split('\n').filter(Boolean);
      return Math.max(1, Math.ceil(lines.length / linesPerPage));
    }

    const words = plainText.split(/\s+/).filter(Boolean);
    return Math.max(1, Math.ceil(words.length / wordsPerPage));
  }, [sermonText, paginationType]);

  const handleDeleteSermon = () => {
    if (sermon) {
      setShowConfirmDelete(true);
    }
  };

  const closeEditor = (targetVerseId = selectedVerseId) => {
    setIsSermonEditorOpen(false);
    if (typeof setSelectedTab === "function" && Array.isArray(columns)) {
      const targetIndex = columns.findIndex((col) =>
        Array.isArray(col.verses) && col.verses.some((verse) => verse.verse_id === targetVerseId)
      );
      setSelectedTab(targetIndex >= 0 ? targetIndex : 0);
    }
  };

  const validateSermonForm = () => {
    const nextErrors = {};
    const trimmedTitle = sermonTitle.trim();
    const trimmedSpeaker = sermonSpeaker.trim();
    const plainContent = stripHtml(sermonText.trim());
    const startRank = getRangeRank(startChapter, startVerseNumber);
    const endRank = getRangeRank(endChapter, endVerseNumber);

    if (!trimmedTitle) nextErrors.title = 'Title is required.';
    if (!trimmedSpeaker) nextErrors.speaker = 'Speaker is required.';
    if (!startChapter) nextErrors.startChapter = 'Start chapter is required.';
    if (!startVerseNumber) nextErrors.startVerse = 'Start verse is required.';
    if (!endChapter) nextErrors.endChapter = 'End chapter is required.';
    if (!endVerseNumber) nextErrors.endVerse = 'End verse is required.';
    if (!plainContent) nextErrors.content = 'Sermon content is required.';

    if (startRank && endRank && endRank < startRank) {
      nextErrors.range = 'End passage must come after the start passage.';
    }

    return nextErrors;
  };

  const handleSaveSermon = async () => {
    const nextErrors = validateSermonForm();
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      setStatusMessage('Fix the highlighted fields and try again.');
      setStatusTone('error');
      return;
    }
    setFormErrors({});
    setStatusMessage(null);

    const normalizedStartChapter = Number.parseInt(startChapter, 10);
    const normalizedEndChapter = Number.parseInt(endChapter, 10);
    const normalizedStartVerse = Number.parseInt(startVerseNumber, 10);
    const normalizedEndVerse = Number.parseInt(endVerseNumber, 10);
    const normalizedStartVerseId = buildVerseId(selectedBookPrefix, normalizedStartChapter, normalizedStartVerse);
    const normalizedEndVerseId = buildVerseId(selectedBookPrefix, normalizedEndChapter, normalizedEndVerse);

    if (!normalizedStartVerseId || !normalizedEndVerseId) {
      setFormErrors({ range: 'Passage range is invalid.' });
      setStatusMessage('Passage range is invalid.');
      setStatusTone('error');
      return;
    }
  
    const sermonData = {
      title: sermonTitle.trim(),
      speaker: sermonSpeaker.trim(),
      content: sermonText.trim(),
      version: selectedVersion,
      book: selectedBook,
      startChapter: normalizedStartChapter,
      startVerseId: normalizedStartVerseId,
      endChapter: normalizedEndChapter,
      endVerseId: normalizedEndVerseId,
    };
  
    try {
      setIsSaving(true);
      let newSermon = null;
      if (sermon && sermon.id) {
        const result = await dispatch(updateSermon({ sermonId: sermon.id, ...sermonData })).unwrap();
        newSermon = { ...sermon, ...result };
      } else {
        const result = await dispatch(addSermon({ verseId: selectedVerseId, ...sermonData })).unwrap();
        newSermon = result;
      }
  
      setColumns((prevColumns) =>
        prevColumns.map((col) => {
          if (col.verses.some((v) => v.verse_id === selectedVerseId)) {
            const updatedSermons = Array.isArray(col.sermons) ? [...col.sermons] : [];
  
            const sermonIndex = updatedSermons.findIndex((s) => s.id === sermon?.id || s.id === newSermon?.id);
            if (sermonIndex !== -1) {
              updatedSermons[sermonIndex] = newSermon;
            } else {
              updatedSermons.push(newSermon);
            }
            return { ...col, sermons: updatedSermons };
          }
          return col;
        })
      );
      setStatusMessage('Sermon saved successfully.');
      setStatusTone('success');
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(draftStorageKey);
      }
      setIsDirty(false); // Reset dirty state after saving
      closeEditor(normalizedStartVerseId);
    } catch (error) {
      console.error('Error saving sermon:', error);
      setStatusMessage('Failed to save the sermon. Please try again.');
      setStatusTone('error');
    } finally {
      setIsSaving(false);
    }
  };
  

  const handleClose = () => {
    if (isDirty) {
      setShowConfirmClose(true);
    } else {
      closeEditor(sermon?.start_verse_id || selectedVerseId);
    }
  };
  
  const confirmClose = () => {
    setShowConfirmClose(false);
    closeEditor(sermon?.start_verse_id || selectedVerseId);
  };

  const cancelClose = () => {
    setShowConfirmClose(false);
  };

  const confirmDeleteSermon = async () => {
    try {
      setIsDeleting(true);
      await dispatch(deleteSermon(sermon.id)).unwrap();

      setColumns((prevColumns) =>
        prevColumns.map((col) => {
          if (col.verses.some((v) => v.verse_id === sermon.start_verse_id)) {
            return {
              ...col,
              sermons: col.sermons.filter((s) => s.id !== sermon.id),
            };
          }
          return col;
        })
      );
      setShowConfirmDelete(false); // Close the modal
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(draftStorageKey);
      }
      closeEditor(sermon.start_verse_id);
    } catch (error) {
      console.error('Error deleting sermon:', error);
      setStatusMessage('Failed to delete the sermon. Please try again.');
      setStatusTone('error');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="mb-4">
        <label className="block text-gray-700">Title:</label>
        <input
          type="text"
          value={sermonTitle}
          onChange={(e) => {
            setSermonTitle(e.target.value);
            setFormErrors((prev) => ({ ...prev, title: null }));
            setStatusMessage(null);
            setIsDirty(true); // Mark as dirty on change
          }}
          placeholder="Enter sermon title"
          className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.title ? 'border-red-500' : ''}`}
        />
        <FieldError message={formErrors.title} />
      </div>

      <div className="mb-4">
        <label className="block text-gray-700">Speaker:</label>
        <input
          type="text"
          value={sermonSpeaker}
          onChange={(e) => {
            setSermonSpeaker(e.target.value);
            setFormErrors((prev) => ({ ...prev, speaker: null }));
            setStatusMessage(null);
            setIsDirty(true); // Mark as dirty on change
          }}
          placeholder="Enter speaker name"
          className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.speaker ? 'border-red-500' : ''}`}
        />
        <FieldError message={formErrors.speaker} />
      </div>

      <div className="mb-4">
        <label className="block text-gray-700 mb-2">Passage:</label>
        <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
          {/* Book Name */}
          <div className="w-full1 sm:w-auto text-lg font-medium text-center sm:text-left">
            {selectedBook}
          </div>

          {/* Chapter and Verses */}
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-1 justify-center">
            {/* Start Chapter and Verse */}
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={startChapter || selectedChapter}
                readOnly
                placeholder="Start Chapter"
                className="w-[40px] px-2 py-1 border rounded-md text-center"
              />
              <span>:</span>
              <input
                type="text"
                value={startVerseNumber}
                readOnly
                placeholder="Start Verse"
                className="w-[40px] px-2 py-1 border rounded-md text-center"
              />
            </div>

            {/* Separator */}
            <div className="text-lg">~</div>

            {/* End Chapter and Verse */}
            <div className="flex items-center gap-1">
              <select
                value={endChapter}
                onChange={(e) => {
                  setEndChapter(e.target.value);
                  setFormErrors((prev) => ({ ...prev, endChapter: null, range: null }));
                  setStatusMessage(null);
                  setIsDirty(true);
                }}
                className={`w-[68px] px-2 py-1 border rounded-md text-center ${formErrors.endChapter || formErrors.range ? 'border-red-500' : ''}`}
              >
                {availableEndChapters.map((chapterOption) => (
                  <option key={chapterOption} value={chapterOption}>
                    {chapterOption}
                  </option>
                ))}
              </select>
              <span>:</span>
              <select
                value={endVerseNumber}
                onChange={(e) => {
                  setEndVerseNumber(e.target.value);
                  setFormErrors((prev) => ({ ...prev, endVerse: null, range: null }));
                  setStatusMessage(null);
                  setIsDirty(true);
                }}
                className={`w-[76px] px-2 py-1 border rounded-md text-center ${formErrors.endVerse || formErrors.range ? 'border-red-500' : ''}`}
              >
                {filteredEndVerseOptions.map((verseOption) => (
                  <option key={verseOption.verseId} value={verseOption.verse}>
                    {verseOption.verse}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <FieldError message={formErrors.range || formErrors.endChapter || formErrors.endVerse || formErrors.startChapter || formErrors.startVerse} />
      </div>

      <div className="mb-4">
        <label className="block text-gray-700">Pagination Type:</label>
        <select
          value={paginationType}
          onChange={(e) => {
            setPaginationType(e.target.value);
            setStatusMessage(null);
            setIsDirty(true);
          }}
          className="w-full px-3 py-2 border rounded-md"
        >
          <option value="word">Word-based</option>
          <option value="line">Line-based</option>
        </select>
        <p className="mt-2 text-sm text-gray-500">
          Estimated pages: {estimatedPageCount}
        </p>
      </div>

      <div className="mb-4  rounded-md h-auto">
        <label className="block text-gray-700 mb-2">Sermon Content:</label>
        <ReactQuill
          ref={quillRef}
          value={sermonText}
          onChange={(content) => {
            setSermonText(content);
            setFormErrors((prev) => ({ ...prev, content: null }));
            setStatusMessage(null);
            setIsDirty(true);
          }}
          placeholder="Start writing your sermon..."
          modules={modules}
        />
        <FieldError message={formErrors.content} />
      </div>

      <StatusBanner tone={statusTone} message={statusMessage} />

      <EditorActions
        hasSermon={Boolean(sermon)}
        isDeleting={isDeleting}
        isSaving={isSaving}
        onClose={handleClose}
        onDelete={handleDeleteSermon}
        onSave={handleSaveSermon}
      />

      {showConfirmDelete && (
        <ConfirmationDialog
          confirmLabel={isDeleting ? 'Deleting...' : 'Yes, Delete'}
          onConfirm={confirmDeleteSermon}
          onCancel={() => setShowConfirmDelete(false)}
        >
          Are you sure you want to delete this sermon? This action cannot be undone.
        </ConfirmationDialog>
      )}
      {/* Close Confirmation Dialog */}
      {showConfirmClose && (
        <ConfirmationDialog
          confirmLabel="Yes, Close"
          onConfirm={confirmClose}
          onCancel={cancelClose}
        >
          You have unsaved changes. Are you sure you want to close without saving?
        </ConfirmationDialog>
      )}

    </>
  );
};

export default SermonEditor;
